import {
  Component,
  OnInit,
  ChangeDetectorRef,
  OnDestroy,
  Input,
  inject,
} from '@angular/core';
import { firstValueFrom, Subscription } from 'rxjs';
import {
  IFilter,
  IGeoJSONCircle,
  IGeoJSONPoint,
  IQueryParam,
  IReporteBase,
  IReporteGenerico,
  IUbicacion,
  IVisitaUbicacion,
} from 'modelos/src';
import { MatDialog } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';
import { DateRange } from '../../../../../../../auxiliares/date-range-selector/date-range-selector.component';
import { LoadingService } from '../../../../../../../auxiliares/servicios/loading.service';
import { HelperService } from '../../../../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../../../../auxiliares/servicios/lista2.service';
import { UbicacionService } from '../../../../../../../auxiliares/servicios/http/ubicacion.service';
import { ReportesGenericosService } from '../../../../../../../auxiliares/servicios/http/reportes-genericos.service';
import { MapaVehiculosService } from '../../../mapaVehiculos.service';
import {
  IVehiculoEnZona,
  IVehiculoPaso,
  IZonaPaso,
  LineStringMapaVehiculos,
  SeleccionadosBusquedaZona,
  TipoBusquedaZona,
} from '../../../interfaces';
import { BusquedaZonaService } from './busqueda-zona.service';
import { RecorridosService } from '../recorridos/recorridos-service';
import { IReporteTracker4G } from 'modelos/src';
@Component({
  selector: 'app-busqueda-zona',
  templateUrl: './busqueda-zona.component.html',
  styleUrl: './busqueda-zona.component.scss',
  standalone: false,
})
export class BusquedaZonaComponent implements OnInit, OnDestroy {
  private lista2 = inject(Lista2Service);
  private mapaService = inject(MapaVehiculosService);
  private busquedaZonaService = inject(BusquedaZonaService);
  private ubicacionService = inject(UbicacionService);
  private reportesGenericosService = inject(ReportesGenericosService);
  loading = inject(LoadingService);
  dialog = inject(MatDialog);
  helper = inject(HelperService);
  private cdr = inject(ChangeDetectorRef);

  /**
   * Contexto en el que se muestra el componente. Lo fija la barra lateral:
   * con una zona seleccionada se busca sobre ella, sin selección se busca por
   * dirección o círculo dibujado.
   */
  @Input() set contextoZona(value: 'ubicacion' | 'sin-seleccion') {
    this._contextoZona = value;
    this.aplicarContexto();
  }
  get contextoZona(): 'ubicacion' | 'sin-seleccion' {
    return this._contextoZona;
  }
  private _contextoZona: 'ubicacion' | 'sin-seleccion' = 'sin-seleccion';

  /** Hay un vehículo seleccionado: habilita la búsqueda de salidas/visitas. */
  @Input() set hayVehiculo(value: boolean) {
    this._hayVehiculo = value;
    this.aplicarContexto();
  }
  get hayVehiculo(): boolean {
    return this._hayVehiculo;
  }
  private _hayVehiculo = false;

  /**
   * Deja el modo y el tipo de búsqueda coherentes con el contexto. Sin nada
   * seleccionado sólo aplica la búsqueda por dirección/círculo; con una zona
   * elegida, la búsqueda sobre esa zona (y las salidas, si hay vehículo).
   */
  private aplicarContexto(): void {
    // "zonas por vehículo" vive ahora en el overlay de Zonas de la búsqueda
    // de viajes, así que acá siempre se busca sobre una zona.
    this.modoBusquedaZona = 'vehiculosPorZona';

    // No hay nada que elegir: cada combinación de selección deja una sola
    // búsqueda con sentido.
    if (this._contextoZona === 'sin-seleccion') {
      // sin selección → por dirección o círculo dibujado
      this.tipoSeleccionado = 'Dirección';
    } else if (this._hayVehiculo) {
      // zona + vehículo → salidas de ESE vehículo a la zona
      this.tipoSeleccionado = 'Visitas';
    } else {
      // sólo zona → todos los vehículos que estuvieron en ella
      this.tipoSeleccionado = 'Ubicación';
    }
  }

  // Toggle entre modos de búsqueda: 'vehiculosPorZona' | 'zonasPorVehiculo'
  public modoBusquedaZona: TipoBusquedaZona = 'vehiculosPorZona';

  //Para el selector de ubicación/dirección (modo: vehiculosPorZona)
  public tipoSeleccionado: string = 'Dirección'; // Valor inicial del selector Dirección/Ubicación
  public direccion: string = '';
  public ubicacion?: IUbicacion;
  public ubicaciones: IUbicacion[] = [];
  public ubicacionId: string | null = null; // para bind con ng-select
  public ubicacion$: Subscription;
  private circuloBusquedaSub$?: Subscription;
  public totalCount = 0;
  public fechaActual = new Date();

  //Mensaje
  public mostrarMensajeSinResultadosVehiculos = false; // Para modo vehículos por zona
  public mostrarMensajeVehiculoRequerido = false; // Para modo zonas por vehículo / visitas
  public mostrarMensajeSinResultadosVisitas = false; // Para tipo Visitas

  // Ocultar mostrar mensajes de información al abrir los banners
  public infoBannerVehiculosCerrado = false;

  public interaccionesBusqueda: IVehiculoPaso[];
  public displayedColumns: string[] = ['vehículos'];
  public interaccionesFiltradas: IVehiculoPaso[] = [];
  /** Lo mismo que interaccionesFiltradas, con los pasos juntos por vehículo. */
  public vehiculosEnZona: IVehiculoEnZona[] = [];

  // Para modo zonas por vehículo
  public vehiculoSeleccionadoId: string | null = null;

  // Para tipo Visitas (dentro de vehiculosPorZona)
  public visitas: IVisitaUbicacion[] = [];
  public gapMin = new FormControl(5); // Gap entre reportes para considerar nueva visita
  public visitaSeleccionada?: IVisitaUbicacion;
  public polylinesVisita: LineStringMapaVehiculos[] = [];
  public cargandoPolyline = false;
  public vueltaResumen?: {
    distanciaKm: number;
    duracionSeg: number;
    maxSpeed: number;
    avgSpeed: number;
  };

  public puntoGeojson: IGeoJSONPoint; //Punto geojson para el buscador
  //Información necesario para la búsqueda
  public buscando: boolean = false; //Determina si se está ejecutando la búsqueda
  public range?: DateRange = this.helper.rangoDiaActual();
  public modoAvanzadoFecha: boolean = false;
  public minutosMinimos = new FormControl(5); // 5 minutos por defecto
  public radio = new FormControl(100); // 100 metros por defecto
  public circuloDeBusqueda: IGeoJSONCircle;
  private currentMapaSeleccionado?: number;
  // Flag para evitar loop circular: subscription → emit → ngOnChanges → setCirculoBusqueda → subscription
  private updatingFromMapCircle = false;

  // Actualiza las interacciones filtradas según los filtros activos
  private actualizarInteraccionesFiltradas(): void {
    this.interaccionesFiltradas = this.busquedaZonaService.filtrarInteracciones(
      this.interaccionesBusqueda,
      this.mapaService.getSeleccionadoActual()?.filtrosSeleccionados,
    );
    this.agruparPorVehiculo();
  }

  /**
   * Un vehículo puede entrar y salir de la zona varias veces en el rango: la
   * lista muestra una tarjeta por vehículo con todos sus lapsos adentro, en
   * vez de repetir el vehículo por cada paso.
   */
  private agruparPorVehiculo(): void {
    const porVehiculo = new Map<string, IVehiculoEnZona>();

    for (const paso of this.interaccionesFiltradas) {
      const id = paso.idVehiculo || paso.vehiculo?._id || '';
      const grupo = porVehiculo.get(id);
      if (grupo) {
        grupo.pasos.push(paso);
      } else {
        porVehiculo.set(id, {
          idVehiculo: id,
          vehiculo: paso.vehiculo,
          pasos: [paso],
        });
      }
    }

    this.vehiculosEnZona = Array.from(porVehiculo.values());
  }

  private seleccionadoActualSub?: Subscription;
  /** Última zona adoptada desde el estado, para no re-procesar cada emisión. */
  private _ultimaUbicacionDelMapaId?: string | null;

  //GETTERS
  public get radioRedondeado(): number | null {
    return this.radio?.value != null
      ? Number(this.radio.value.toFixed(0))
      : null;
  }

  // Construye el objeto de estado consolidado
  private buildSeleccionados(
    override?: Partial<SeleccionadosBusquedaZona>,
  ): SeleccionadosBusquedaZona {
    return {
      modoBusquedaZona: this.modoBusquedaZona,
      tipoBusqueda: this.tipoSeleccionado,
      direccion: this.direccion,
      ubicacion: this.ubicacion,
      circulo: this.circuloDeBusqueda || undefined,
      range: this.range,
      tiempoEnZona: this.minutosMinimos?.value,
      gapMin: this.gapMin?.value,
      idVehiculo: this.vehiculoSeleccionadoId,
      modoAvanzado: this.modoAvanzadoFecha,
      busquedaVehiculosPorZona: this.interaccionesBusqueda?.length
        ? { vehiculosPasos: this.interaccionesBusqueda }
        : undefined,
      busquedaVisitas:
        this.visitas?.length && this.vehiculoSeleccionadoId && this.ubicacion
          ? {
              idVehiculo: this.vehiculoSeleccionadoId,
              ubicacion: this.ubicacion,
              visitas: this.visitas,
              polylines: this.polylinesVisita?.length
                ? this.polylinesVisita
                : undefined,
              centrar: this.polylinesVisita?.[0]?.pointsRecorrido?.[0],
              visitaSeleccionadaKey: this.visitaSeleccionada?.entrada,
            }
          : undefined,
      ...override,
    };
  }

  // Emite el estado consolidado
  private emitSeleccionados(
    override?: Partial<SeleccionadosBusquedaZona>,
  ): void {
    const seleccionados = this.buildSeleccionados(override);
    const puedeDibujar =
      seleccionados.modoBusquedaZona === 'vehiculosPorZona' &&
      seleccionados.tipoBusqueda === 'Dirección';
    this.mapaService.updateSeleccionadoBarraLateral({
      seleccionadosVehiculoZona: seleccionados,
      puedeDibujar,
    });
  }

  //LISTAR
  public async listarUbicaciones(): Promise<void> {
    const query: IQueryParam = {
      filter: '',
      sort: 'nombre',
      includeChildren: true,
    };
    const filter: IFilter<IUbicacion> = query.filter
      ? JSON.parse(query.filter)
      : {};
    filter.geojson = { $exists: true };
    filter.categoria = 'Vehiculos';
    query.filter = JSON.stringify(filter);
    //
    this.ubicacion$?.unsubscribe();
    this.ubicacion$ = this.lista2.ubicacions(query, 30).subscribe((data) => {
      this.totalCount = data.totalCount;
      this.ubicaciones = data.datos;

      // Si ya tenemos una ubicacionId restaurada, intentar rellenar el objeto ubicacion
      if (this.ubicacionId && !this.ubicacion) {
        const found = this.ubicaciones.find((u) => u._id === this.ubicacionId);
        if (found) {
          this.onChangeUbicacion(found);
        }
      }
    });
  }

  //FUNCIONES
  public onChangeUbicacion(event: IUbicacion | string) {
    // event puede ser el objeto IUbicacion (cuando cambia por selección) o el id (si ng-select devuelve id)
    let selected: IUbicacion | undefined;
    if (typeof event === 'string') {
      selected = this.ubicaciones.find((u) => u._id === event);
    } else {
      selected = event as IUbicacion;
    }
    if (!selected) return;
    this.ubicacion = selected;
    this.ubicacionId = selected._id;
    this.direccion = selected.direccion;

    // La zona es contexto de búsqueda: el mapa la resalta y el panel se
    // sincroniza con ella la elija el usuario acá o clickeándola en el mapa.
    this._ultimaUbicacionDelMapaId = selected._id;
    this.mapaService.updateSeleccionadoBarraLateral({
      ubicacionSeleccionada: selected,
    });

    // Limpiar cualquier círculo previo del mapa
    // updatingFromMapCircle evita que la suscripción limpie estado antes de que este método termine
    this.updatingFromMapCircle = true;
    this.mapaService.setCirculoBusqueda(undefined);
    this.updatingFromMapCircle = false;
    this.circuloDeBusqueda = null;

    // Dibujar el polígono real de la ubicación en el mapa
    // (el mapa se encarga de hacer fitBounds proporcional al polígono)
    this.mapaService.setPoligonoBusqueda(selected);

    // Emitir cambios
    this.emitSeleccionados({ busquedaVehiculosPorZona: undefined });
  }

  public onRadioChange() {
    // Redondear el valor del radio
    if (this.radio?.value != null) {
      const valorRedondeado = Math.round(this.radio.value);
      this.radio.setValue(valorRedondeado, { emitEvent: false });
    }
    // Actualizar circuloDeBusqueda con el nuevo radio
    if (this.circuloDeBusqueda?.coordinates && this.radio?.value > 0) {
      this.circuloDeBusqueda = {
        ...this.circuloDeBusqueda,
        radius: this.radio.value,
      };
      this.emitSeleccionados();
      this.sincronizarCirculoAlMapa();
    }
  }

  public emitirCambios() {
    // Emitir los valores actuales cuando cambian desde, horas o tiempo en zona
    if (this.circuloDeBusqueda?.coordinates && this.radio?.value > 0) {
      this.emitSeleccionados();
    }
  }

  /**
   * La zona con la que se busca sale del estado, que es la fuente de verdad
   * (es lo que el mapa resalta y lo que muestra el chip del panel). El campo
   * local es una copia y cualquier reset del formulario la puede perder, así
   * que se re-sincroniza en el momento de buscar.
   */
  private asegurarUbicacionDelContexto(): boolean {
    const delEstado =
      this.mapaService.getSeleccionadoActual()?.ubicacionSeleccionada;
    if (delEstado?._id && this.ubicacion?._id !== delEstado._id) {
      this.ubicacion = delEstado;
      this.ubicacionId = delEstado._id;
      this._ultimaUbicacionDelMapaId = delEstado._id;
      this.direccion = delEstado.direccion ?? this.direccion;
    }
    return !!this.ubicacion?._id;
  }

  /** Arranca (o retoma) el dibujo del círculo sobre el mapa seleccionado. */
  public dibujarEnMapa(): void {
    this.mapaService.pedirDibujoCirculo();
  }

  public buscar() {
    if (this.tipoSeleccionado === 'Ubicación') {
      this.buscarPorUbicacion();
    } else if (this.tipoSeleccionado === 'Visitas') {
      this.buscarVisitas();
    } else {
      this.circuloDeBusquedaChange();
    }
  }

  public direccionAutocompleteChange(geoJSON: IGeoJSONPoint) {
    this.circuloDeBusqueda = {
      type: 'Point',
      coordinates: geoJSON.coordinates,
      radius: this.radio.value,
    };
    this.emitSeleccionados();
    this.sincronizarCirculoAlMapa();
  }

  // Sincroniza el círculo actual con el servicio para que el mapa lo dibuje y panee
  private sincronizarCirculoAlMapa() {
    if (
      this.circuloDeBusqueda?.coordinates &&
      this.circuloDeBusqueda.radius > 0
    ) {
      this.mapaService.setCirculoBusqueda(this.circuloDeBusqueda);
      this.mapaService.emitPanOnce(
        this.circuloDeBusqueda.coordinates as [number, number],
        this.circuloDeBusqueda.radius,
      );
    }
  }

  // Se ejecuta cuando cambia el tipo de búsqueda entre Dirección/Ubicación/Visitas
  public onTipoSeleccionadoChange() {
    // Ocultar círculo y polígono del mapa
    // updatingFromMapCircle evita que la suscripción a circuloBusqueda$ limpie circuloDeBusqueda
    // antes de que la lógica de restauración (más abajo) pueda usarlo
    this.updatingFromMapCircle = true;
    this.mapaService.setCirculoBusqueda(undefined);
    this.mapaService.setPoligonoBusqueda(undefined);
    this.updatingFromMapCircle = false;

    // Limpiar resultados de visitas al cambiar de tipo (se vuelven irrelevantes)
    this.visitas = [];
    this.visitaSeleccionada = undefined;
    this.polylinesVisita = [];
    this.mostrarMensajeSinResultadosVisitas = false;

    // Limpiar campos del tipo que se abandona, preservando los del otro
    if (this.tipoSeleccionado === 'Dirección') {
      // Volviendo a Dirección: limpiar ubicación, restaurar círculo y dirección
      this.ubicacion = undefined;
      this.ubicacionId = null;
      if (this.circuloDeBusqueda?.coordinates) {
        this.radio.setValue(Math.round(this.circuloDeBusqueda.radius), {
          emitEvent: false,
        });
        this.mapaService.setCirculoBusqueda(this.circuloDeBusqueda);
      }
      // this.direccion se preserva (no limpiar)
    } else if (this.tipoSeleccionado === 'Ubicación') {
      // Yendo a Ubicación: limpiar ubicación previa, preservar dirección y círculo
      this.ubicacion = undefined;
      this.ubicacionId = null;
    } else if (this.tipoSeleccionado === 'Visitas') {
      // Yendo a Visitas: limpiar ubicación previa
      this.ubicacion = undefined;
      this.ubicacionId = null;
    }
    // El visual ya está oculto por setCirculoBusqueda(undefined) arriba.

    this.emitSeleccionados();
  }

  // BÚSQUEDA POR UBICACIÓN (usa el polígono real de la ubicación)
  public async buscarPorUbicacion() {
    if (!this.asegurarUbicacionDelContexto() || !this.ubicacion?.geojson) {
      this.interaccionesBusqueda = [];
      this.interaccionesFiltradas = [];
      this.helper.notifError('Seleccioná una zona en el mapa para buscar');
      return;
    }
    if (!this.range?.start) {
      this.helper.notifError('Elegí un rango de fechas para buscar');
      return;
    }

    const mapaInicioBusqueda = this.currentMapaSeleccionado;
    this.fechaActual = new Date();
    this.buscando = true;
    this.mapaService.setSearchInProgress(true);
    this.mostrarMensajeSinResultadosVehiculos = false;

    try {
      const pasos = await this.reportesGenericosService.buscarVehiculosEnZona({
        geoWithin: { $geometry: this.ubicacion.geojson },
        rango: { $gte: this.range.start, $lte: this.range.end },
        // La zona es de un cliente: se buscan los vehículos de ese cliente y
        // los de sus hijos, no los de todo el árbol del permiso.
        clienteId: this.ubicacion.idCliente,
        includeChildren: true,
        minutosMinimos: this.minutosMinimos?.value ?? 0,
      });

      // Verificar que no cambió el mapa mientras se buscaba
      if (mapaInicioBusqueda !== this.currentMapaSeleccionado) {
        this.buscando = false;
        this.mapaService.setSearchInProgress(false);
        return;
      }

      this.buscando = false;
      this.mapaService.setSearchInProgress(false);

      this.interaccionesBusqueda = pasos;
      this.actualizarInteraccionesFiltradas();

      // El mensaje mira lo que se va a mostrar, no lo que trajo el backend:
      // si los filtros descartan todo, hay que decirlo igual.
      this.mostrarMensajeSinResultadosVehiculos =
        this.interaccionesFiltradas.length === 0;
      this.cdr.detectChanges();

      // Emitir resultados
      this.emitSeleccionados({
        busquedaVehiculosPorZona: {
          vehiculosPasos: this.interaccionesBusqueda,
        },
      });
    } catch (error) {
      console.error('Error buscando por ubicación:', error);
      this.buscando = false;
      this.mapaService.setSearchInProgress(false);
      this.interaccionesBusqueda = [];
      this.mostrarMensajeSinResultadosVehiculos = true;
      this.cdr.detectChanges();
    }
  }

  // BÚSQUEDA DE VISITAS A UBICACIÓN (vehículo + ubicación + día)
  public async buscarVisitas() {
    // Vehículo desde el seleccionado en el mapa
    const seleccionado = this.mapaService.getSeleccionadoActual();
    const reporteSel = seleccionado?.reporteSeleccionado;
    if (reporteSel?.activo?._id) {
      this.vehiculoSeleccionadoId = reporteSel.activo._id;
    }

    if (!this.vehiculoSeleccionadoId) {
      this.mostrarMensajeVehiculoRequerido = true;
      this.cdr.detectChanges();
      return;
    }
    this.mostrarMensajeVehiculoRequerido = false;

    // Nunca fallar en silencio: sin zona o sin rango la lupa no hacía nada
    if (!this.asegurarUbicacionDelContexto()) {
      this.helper.notifError('Seleccioná una zona en el mapa para buscar');
      return;
    }
    if (!this.range?.start || !this.range?.end) {
      this.helper.notifError('Elegí un rango de fechas para buscar');
      return;
    }

    // Asegurar que el polígono de la ubicación esté visible en el mapa
    this.mapaService.setPoligonoBusqueda(this.ubicacion);

    const mapaInicioBusqueda = this.currentMapaSeleccionado;
    this.fechaActual = new Date();
    this.buscando = true;
    this.mapaService.setSearchInProgress(true);
    this.mostrarMensajeSinResultadosVisitas = false;

    try {
      const visitas = await this.ubicacionService.getVisitas(
        this.ubicacion._id,
        {
          idVehiculo: this.vehiculoSeleccionadoId,
          from: this.range.start,
          to: this.range.end,
          gapMin: this.gapMin?.value ?? 5,
        },
      );

      if (mapaInicioBusqueda !== this.currentMapaSeleccionado) {
        return;
      }

      this.visitas = visitas ?? [];
      this.mostrarMensajeSinResultadosVisitas = this.visitas.length === 0;
      this.cdr.detectChanges();

      this.emitSeleccionados({
        busquedaVisitas: this.visitas.length
          ? {
              idVehiculo: this.vehiculoSeleccionadoId,
              ubicacion: this.ubicacion,
              visitas: this.visitas,
            }
          : undefined,
      });
    } catch (error) {
      console.error('Error buscando visitas:', error);
      this.visitas = [];
      this.mostrarMensajeSinResultadosVisitas = true;
      this.cdr.detectChanges();
    } finally {
      this.buscando = false;
      this.mapaService.setSearchInProgress(false);
    }
  }

  public async seleccionarVisita(v: IVisitaUbicacion) {
    if (this.visitaSeleccionada?.entrada === v.entrada) {
      this.visitaSeleccionada = undefined;
      this.polylinesVisita = [];
      this.vueltaResumen = undefined;
      this.emitSeleccionados();
      return;
    }
    this.visitaSeleccionada = v;
    this.vueltaResumen = undefined;
    if (v?.entrada && this.vehiculoSeleccionadoId) {
      await this.dibujarVueltaVisita(v);
    } else {
      this.polylinesVisita = [];
      this.emitSeleccionados();
    }
  }

  // Dibuja "la vuelta": recorrido desde el último adentro de la visita anterior
  // (o inicio del día) hasta el primer adentro de esta visita.
  private async dibujarVueltaVisita(v: IVisitaUbicacion) {
    if (!this.vehiculoSeleccionadoId || !v.entrada) return;
    const idx = this.visitas.findIndex((x) => x.entrada === v.entrada);
    const prev = idx > 0 ? this.visitas[idx - 1] : undefined;
    const desde = prev?.salida || this.range?.start;
    const hasta = v.entrada;
    if (!desde || !hasta) {
      this.polylinesVisita = [];
      this.emitSeleccionados();
      return;
    }

    this.cargandoPolyline = true;
    this.cdr.detectChanges();
    try {
      const filter: IFilter<IReporteGenerico> = {
        tipoEntidad: 'Vehiculo',
        idsAsignados: this.vehiculoSeleccionadoId,
        tipoReporte: {
          $in: ['Tracker 4G', 'Tracker Qualcomm', 'Tracker T1000B'],
        },
        'valores.timestamp': {
          $gte: desde,
          $lte: hasta,
        },
      } as any;

      const queryPuntos: IQueryParam = {
        filter: JSON.stringify(filter),
        sort: '-valores.timestamp',
        includeChildren: true,
      };
      const queryReportes: IQueryParam = {
        filter: JSON.stringify(filter),
        select: 'valores',
        sort: 'valores.timestamp',
        includeChildren: true,
      };

      const vialPath = await firstValueFrom(
        this.lista2.vialPathGenericos(queryPuntos),
      );
      const reportes = await firstValueFrom(
        this.lista2.reportesGenericos<
          'Tracker 4G' | 'Tracker Qualcomm' | 'Tracker T1000B'
        >(['Tracker 4G', 'Tracker Qualcomm', 'Tracker T1000B'], queryReportes),
      );

      if (
        !vialPath?.coordsVialPath?.length ||
        !vialPath?.coordsSnapped?.length ||
        !reportes?.datos?.length
      ) {
        this.polylinesVisita = [];
        this.vueltaResumen = undefined;
      } else {
        const datos = reportes.datos as IReporteBase<
          'Tracker 4G' | 'Tracker T1000B' | 'Tracker Qualcomm'
        >[];
        this.polylinesVisita = RecorridosService.armarRecorridos(
          vialPath.coordsVialPath,
          vialPath.coordsSnapped,
          datos,
        );
        this.vueltaResumen = this.calcularResumenVuelta(datos, desde, hasta);
      }
      this.emitSeleccionados();
    } catch (err) {
      console.error('[VISITAS] Error dibujando polyline de visita', err);
      this.polylinesVisita = [];
      this.vueltaResumen = undefined;
    } finally {
      this.cargandoPolyline = false;
      this.cdr.detectChanges();
    }
  }

  private calcularResumenVuelta(
    reportes: IReporteBase<
      'Tracker 4G' | 'Tracker T1000B' | 'Tracker Qualcomm'
    >[],
    desde: string,
    hasta: string,
  ): {
    distanciaKm: number;
    duracionSeg: number;
    maxSpeed: number;
    avgSpeed: number;
  } {
    const distM = RecorridosService.calcularDistanciaPorOdometro(reportes);
    const distanciaKm = distM != null && distM > 0 ? distM / 1000 : 0;
    const velocidades = reportes
      .map((r) => (r.valores as IReporteTracker4G)?.velocidad ?? 0)
      .filter((v) => v > 0);
    const maxSpeed = velocidades.length ? Math.max(...velocidades) : 0;
    const avgSpeed = velocidades.length
      ? velocidades.reduce((a, b) => a + b, 0) / velocidades.length
      : 0;
    const duracionSeg = Math.max(
      0,
      Math.round(
        (new Date(hasta).getTime() - new Date(desde).getTime()) / 1000,
      ),
    );
    return { distanciaKm, duracionSeg, maxSpeed, avgSpeed };
  }

  //FUNCIÓN DE BÚSQUEDA
  public async circuloDeBusquedaChange() {
    if (this.radio?.value <= 0) return;

    if (this.circuloDeBusqueda?.coordinates && this.range.start) {
      const mapaInicioBusqueda = this.currentMapaSeleccionado;
      this.fechaActual = new Date();
      this.buscando = true;
      this.mostrarMensajeSinResultadosVehiculos = false;

      //Radio
      const radioEnMetros = this.radio.value; // Radio en metros
      const radioEnKm = radioEnMetros / 1000; // Convertir a kilómetros
      const radioEnRadianes = radioEnKm / 6378.1; // Radio de la Tierra en km

      const pasos = await this.reportesGenericosService.buscarVehiculosEnZona({
        geoWithin: {
          $centerSphere: [
            this.circuloDeBusqueda.coordinates, // Centro del círculo en lon/lat
            radioEnRadianes, // Radio en radianes
          ],
        },
        rango: { $gte: this.range.start, $lte: this.range.end },
        includeChildren: true,
        minutosMinimos: this.minutosMinimos?.value ?? 0,
      });

      // Verificar que no cambió el mapa mientras se buscaba
      if (mapaInicioBusqueda !== this.currentMapaSeleccionado) {
        this.buscando = false;
        return;
      }

      this.buscando = false;

      this.interaccionesBusqueda = pasos;
      this.actualizarInteraccionesFiltradas();

      // El mensaje mira lo que se va a mostrar, no lo que trajo el backend
      this.mostrarMensajeSinResultadosVehiculos =
        this.interaccionesFiltradas.length === 0;
      this.cdr.detectChanges();

      // Emitir resultados
      this.emitSeleccionados({
        busquedaVehiculosPorZona: {
          vehiculosPasos: this.interaccionesBusqueda,
        },
      });
    } else {
      this.interaccionesBusqueda = [];
      // Emitir estado vacío
      this.emitSeleccionados({ busquedaVehiculosPorZona: undefined });
    }
  }

  // Limpiar solo resultados de zonas por vehículo, preservando el círculo/polígono
  // y los resultados de vehículos por zona. Usado al cambiar de vehículo.
  public limpiarResultados(): void {
    this.vehiculoSeleccionadoId = null;
    this.mostrarMensajeVehiculoRequerido = false;
    this.buscando = false;

    this.emitSeleccionados();
  }

  // RESETEAR BÚSQUEDA
  public resetear(): void {
    // Limpiar círculo y polígono del mapa y servicio
    // updatingFromMapCircle evita que la suscripción interfiera; este método limpia todo manualmente
    this.updatingFromMapCircle = true;
    this.mapaService.setCirculoBusqueda(undefined);
    this.mapaService.setPoligonoBusqueda(undefined);
    this.updatingFromMapCircle = false;

    // Resetear campos de vehículos por zona
    this.direccion = '';
    this.ubicacion = undefined;
    this.ubicacionId = null;
    this.tipoSeleccionado = 'Dirección';
    this.circuloDeBusqueda = null;
    this.range = this.helper.rangoDiaActual();
    this.minutosMinimos?.setValue(5, { emitEvent: false });
    this.gapMin?.setValue(5, { emitEvent: false });
    this.radio?.setValue(100, { emitEvent: false });
    this.interaccionesBusqueda = [];
    this.interaccionesFiltradas = [];

    // Resetear campos de zonas por vehículo
    this.vehiculoSeleccionadoId = null;
    this.mostrarMensajeVehiculoRequerido = false;

    // Resetear campos de visitas
    this.visitas = [];
    this.visitaSeleccionada = undefined;
    this.polylinesVisita = [];
    this.mostrarMensajeSinResultadosVisitas = false;

    this.buscando = false;
    this.mostrarMensajeSinResultadosVehiculos = false;

    // Emitir búsqueda vacía
    this.emitSeleccionados({
      busquedaVehiculosPorZona: undefined,
      busquedaVisitas: undefined,
    });
  }

  //EXPORTACIÓN DE VEHÍCULOS

  public async exportarVehiculos() {
    // Se exporta lo que se está viendo: interaccionesBusqueda trae también los
    // vehículos de los clientes hijos que el filtro del panel oculta.
    await this.busquedaZonaService.exportarVehiculos(
      this.interaccionesFiltradas,
      this.origenDeLaBusqueda(),
      // El radio sólo aplica al círculo; sobre una zona el límite es su polígono
      this.contextoZona === 'ubicacion' ? null : this.radio.value,
      this.minutosMinimos.value,
      this.range,
    );
  }

  /** Qué se usó como centro/límite de la búsqueda, para la columna Origen. */
  private origenDeLaBusqueda(): string {
    if (this.contextoZona === 'ubicacion') {
      return (
        this.ubicacion?.identificacion ||
        this.ubicacion?.direccion ||
        'Zona seleccionada'
      );
    }
    if (this.direccion) return this.direccion;
    const coords = this.circuloDeBusqueda?.coordinates;
    if (coords) {
      return `Círculo dibujado (${coords[1].toFixed(5)}, ${coords[0].toFixed(5)})`;
    }
    return '';
  }

  public formatearMinutos(minutos: number): string {
    return RecorridosService.formatearMinutos(minutos);
  }

  public formatearDuracion(durationSec: number): string {
    return RecorridosService.formatearDuracion(durationSec);
  }

  //HOOKS
  async ngOnInit() {
    await this.listarUbicaciones();

    // Subscribirse a cambios en el círculo de búsqueda desde el servicio
    this.circuloBusquedaSub$ = this.mapaService.circuloBusqueda$.subscribe(
      (circulo) => {
        //Evita bucle infinito entre cambios del círculo en este componente de búsqueda y cambios desde el mapa
        if (this.updatingFromMapCircle) return;
        if (circulo && circulo.radius != null && circulo.coordinates) {
          // Actualizar el círculo de este componente de búsqueda
          this.circuloDeBusqueda = circulo;
          // Actualizar el radio en el formulario solo si viene del servicio
          const radioRedondeado = Math.round(circulo.radius);
          this.radio?.setValue(radioRedondeado, { emitEvent: false });
          // Emitir cambios para persistir en el estado
          this.updatingFromMapCircle = true;
          this.emitSeleccionados();
          Promise.resolve().then(() => {
            this.updatingFromMapCircle = false;
          });
        } else if (!circulo || !circulo.coordinates) {
          // Círculo eliminado desde el mapa (borrarDibujos).
          // Solo limpiar si estamos activamente en la solapa zona.
          // Si viene de un cambio de solapa, NO destruir el estado local
          // para que pueda restaurarse al volver a la solapa zona.
          const sel = this.mapaService.getSeleccionadoActual();
          if (sel?.tipoBusqueda === 'zona') {
            this.circuloDeBusqueda = null;
            this.updatingFromMapCircle = true;
            this.emitSeleccionados({ busquedaVehiculosPorZona: undefined });
            Promise.resolve().then(() => {
              this.updatingFromMapCircle = false;
            });
          }
        }
      },
    );

    // Asegurar que los valores por defecto se establezcan después de la inicialización
    setTimeout(() => {
      // Establecer fecha por defecto
      if (!this.range?.start) {
        this.range = this.helper.rangoDiaActual();
      }
      if (!this.radio?.value) {
        this.radio?.setValue(100, { emitEvent: false });
      }
      if (!this.minutosMinimos?.value) {
        this.minutosMinimos?.setValue(5, { emitEvent: false });
      }
      // Forzar detección de cambios para mostrar valores por defecto
      this.cdr.detectChanges();
    }, 100);

    this.seleccionadoActualSub = this.mapaService.seleccionadoActual$.subscribe(
      (seleccionado) => {
        if (!seleccionado) {
          this.circuloDeBusqueda = null;
          this.tipoSeleccionado = 'Dirección';
          this.direccion = '';
          this.ubicacion = undefined;
          this.ubicacionId = null;
          this.range = this.helper.rangoDiaActual();
          this.minutosMinimos?.setValue(5, { emitEvent: false });
          this.radio?.setValue(100, { emitEvent: false });
          this.interaccionesBusqueda = [];
          this.interaccionesFiltradas = [];
          this.vehiculoSeleccionadoId = null;
          this.currentMapaSeleccionado = undefined;
          return;
        }

        this.actualizarInteraccionesFiltradas();

        const prevMapa = this.currentMapaSeleccionado;
        this.currentMapaSeleccionado = seleccionado.mapaSeleccionado;

        // ZONA SELECCIONADA EN EL MAPA
        // Se adopta como ubicación de la búsqueda. Se corta acá porque
        // onChangeUbicacion/emitSeleccionados re-emiten el estado y esa
        // re-entrada restaura los campos con el estado ya actualizado; seguir
        // con el estado viejo de esta emisión pisaría la adopción.
        const ubicacionDelMapa = seleccionado.ubicacionSeleccionada;
        const idUbicacionDelMapa = ubicacionDelMapa?._id ?? null;
        // También se re-adopta si el formulario perdió la zona: cualquier
        // emisión sin seleccionadosVehiculoZona resetea el formulario, y con el
        // guard por id solo la zona quedaba caída para siempre (la búsqueda de
        // salidas cortaba en silencio por falta de ubicación).
        const formularioDesincronizado =
          (this.ubicacion?._id ?? null) !== idUbicacionDelMapa;
        if (
          idUbicacionDelMapa !== (this._ultimaUbicacionDelMapaId ?? null) ||
          formularioDesincronizado
        ) {
          this._ultimaUbicacionDelMapaId = idUbicacionDelMapa;

          if (ubicacionDelMapa) {
            this.modoBusquedaZona = 'vehiculosPorZona';
            this.onChangeUbicacion(ubicacionDelMapa);
            this.aplicarContexto();
            return;
          }

          if (this.ubicacionId) {
            this.ubicacion = undefined;
            this.ubicacionId = null;
            this.mapaService.setPoligonoBusqueda(undefined);
            this.emitSeleccionados({ busquedaVehiculosPorZona: undefined });
            return;
          }
        }

        const busquedaZona = seleccionado.seleccionadosVehiculoZona;

        if (busquedaZona) {
          this.modoBusquedaZona =
            busquedaZona.modoBusquedaZona || 'vehiculosPorZona';
          this.modoAvanzadoFecha = busquedaZona.modoAvanzado ?? false;

          if (
            this.modoBusquedaZona === 'vehiculosPorZona' &&
            busquedaZona.tipoBusqueda
          ) {
            const tipoRestaurado = busquedaZona?.tipoBusqueda || 'Dirección';
            this.tipoSeleccionado =
              tipoRestaurado === 'Círculo' ? 'Dirección' : tipoRestaurado;
            // El contexto manda sobre lo guardado: un estado viejo no puede
            // dejar el panel en un tipo que no aplica a lo que hay seleccionado
            this.aplicarContexto();
            this.direccion = busquedaZona?.direccion || '';
            this.ubicacion = busquedaZona?.ubicacion || undefined;
            this.ubicacionId = this.ubicacion?._id ?? null;
            // Un rango a medio armar ({} o sin start) vaciaba el selector
            this.range = busquedaZona?.range?.start
              ? busquedaZona.range
              : this.helper.rangoDiaActual();
            this.minutosMinimos?.setValue(busquedaZona?.tiempoEnZona ?? 5, {
              emitEvent: false,
            });
            this.gapMin?.setValue(busquedaZona?.gapMin ?? 5, {
              emitEvent: false,
            });

            if (busquedaZona?.circulo) {
              this.circuloDeBusqueda = busquedaZona.circulo;
              this.radio?.setValue(Math.round(busquedaZona.circulo.radius), {
                emitEvent: false,
              });
            } else {
              this.circuloDeBusqueda = null;
              this.radio?.setValue(100, { emitEvent: false });
            }

            if (seleccionado.tipoBusqueda === 'zona') {
              this.updatingFromMapCircle = true;
              const polActual = this.mapaService.poligonoBusqueda;
              if (
                (this.tipoSeleccionado === 'Ubicación' ||
                  this.tipoSeleccionado === 'Visitas') &&
                busquedaZona?.ubicacion?.geojson
              ) {
                // Solo re-emitir si cambió la ubicación, evita re-fitBounds en cada tick
                if (polActual?._id !== busquedaZona.ubicacion._id) {
                  this.mapaService.setPoligonoBusqueda(busquedaZona.ubicacion);
                }
              } else if (
                this.tipoSeleccionado === 'Dirección' &&
                busquedaZona?.circulo?.coordinates
              ) {
                if (polActual) {
                  this.mapaService.setPoligonoBusqueda(undefined);
                }
                this.circuloDeBusqueda = busquedaZona.circulo;
                this.mapaService.setCirculoBusqueda(busquedaZona.circulo);
              } else {
                this.mapaService.setCirculoBusqueda(undefined);
                if (polActual) {
                  this.mapaService.setPoligonoBusqueda(undefined);
                }
              }
              Promise.resolve().then(() => {
                this.updatingFromMapCircle = false;
              });
            }

            if (busquedaZona?.busquedaVehiculosPorZona?.vehiculosPasos) {
              this.interaccionesBusqueda =
                busquedaZona.busquedaVehiculosPorZona.vehiculosPasos;
              this.actualizarInteraccionesFiltradas();
            } else {
              this.interaccionesBusqueda = [];
              this.interaccionesFiltradas = [];
            }

            if (busquedaZona?.busquedaVisitas?.visitas) {
              this.visitas = busquedaZona.busquedaVisitas.visitas;
              this.vehiculoSeleccionadoId =
                busquedaZona.busquedaVisitas.idVehiculo ?? null;
              this.polylinesVisita =
                busquedaZona.busquedaVisitas.polylines ?? [];
              const key = busquedaZona.busquedaVisitas.visitaSeleccionadaKey;
              this.visitaSeleccionada = key
                ? this.visitas.find((v) => v.entrada === key)
                : undefined;
            } else {
              this.visitas = [];
              this.visitaSeleccionada = undefined;
              this.polylinesVisita = [];
            }
          }

          this.cdr.detectChanges();
        }

        if (!busquedaZona) {
          this.circuloDeBusqueda = null;
          this.modoBusquedaZona = 'vehiculosPorZona';
          this.direccion = '';
          // La zona seleccionada en el mapa NO se resetea: es contexto, no
          // resultado de una búsqueda. Antes se perdía y el formulario quedaba
          // en Dirección aunque el mapa siguiera con la zona marcada.
          this.ubicacion = seleccionado.ubicacionSeleccionada;
          this.ubicacionId = this.ubicacion?._id ?? null;
          this._ultimaUbicacionDelMapaId = this.ubicacionId;
          this.aplicarContexto();
          this.range = this.helper.rangoDiaActual();
          this.modoAvanzadoFecha = false;
          this.minutosMinimos?.setValue(5, { emitEvent: false });
          this.gapMin?.setValue(5, { emitEvent: false });
          this.radio?.setValue(100, { emitEvent: false });
          this.interaccionesBusqueda = [];
          this.interaccionesFiltradas = [];
          this.vehiculoSeleccionadoId = null;
          this.visitas = [];
          this.visitaSeleccionada = undefined;
          this.polylinesVisita = [];
          this.mostrarMensajeSinResultadosVehiculos = false;
          this.mostrarMensajeSinResultadosVisitas = false;
          this.mostrarMensajeVehiculoRequerido = false;
          this.updatingFromMapCircle = true;
          this.mapaService.setCirculoBusqueda(undefined);
          this.mapaService.setPoligonoBusqueda(undefined);
          Promise.resolve().then(() => {
            this.updatingFromMapCircle = false;
          });
          this.cdr.detectChanges();
        }
      },
    );
  }

  ngOnDestroy() {
    this.ubicacion$?.unsubscribe();
    this.circuloBusquedaSub$?.unsubscribe();
    this.seleccionadoActualSub?.unsubscribe();
  }
}
